<script setup>
import {
    RadarController,
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
    Chart
} from 'chart.js';

import { Radar } from 'vue-chartjs';
import { computed, ref, onBeforeUnmount } from 'vue';

Chart.register(
    RadarController,
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend
);

const chartRef = ref(null);

const props = defineProps({
    scores: { type: Object, required: true },
    domainMap: { type: Object, required: true }
});

const chartScoreList = computed(() => {
    return Object.entries(props.scores).map(([label, value]) => ({
        label,
        value
    }));
});

// mobile breakpoint
const isMobile = ref(window.innerWidth < 640);

const updateMobileState = () => {
    isMobile.value = window.innerWidth < 640;
};

window.addEventListener('resize', updateMobileState);

onBeforeUnmount(() => {
    window.removeEventListener('resize', updateMobileState);
});

const labels = computed(() => chartScoreList.value.map(i => i.label));
const dataValues = computed(() => chartScoreList.value.map(i => i.value));

/**
 * Domain color mapping
 */
const domainColorMap = {
    Identity: 'rgba(255, 99, 132, 0.15)',
    Readiness: 'rgba(54, 162, 235, 0.15)',
    Execution: 'rgba(255, 206, 86, 0.15)',
    Courage: 'rgba(75, 192, 192, 0.15)'
};

const getDomain = (sub) => props.domainMap[sub] || 'Unknown';

/**
 * Radar data
 */
const chartData = computed(() => ({
    labels: labels.value,
    datasets: [
        {
            label: 'Assessment Scores',
            data: dataValues.value,
            backgroundColor: 'rgba(99, 102, 241, 0.2)',
            borderColor: '#6366f1',
            borderWidth: 3,
            pointRadius: 5,
            pointBackgroundColor: '#6366f1'
        }
    ]
}));

/**
 * Background shading plugin
 */
const colorfulSectorsPlugin = {
    id: 'colorfulSectors',
    beforeDraw(chart) {
        const { ctx, scales } = chart;
        
        const rScale = scales.r;
        const cx = rScale.xCenter;
        const cy = rScale.yCenter;

        const total = labels.value.length;
        const angleStep = (Math.PI * 2) / total;
        const radius = rScale.getDistanceFromCenterForValue(rScale.max);

        let grouped = {};

        labels.value.forEach((sub, i) => {
            const domain = getDomain(sub);
            if (!grouped[domain]) {
                grouped[domain] = [];
            }
            grouped[domain].push(i);
        });

        Object.keys(grouped).forEach(domain => {
            const idx = grouped[domain];
            const start = idx[0];
            const end = idx[idx.length - 1];

            const startAngle = start * angleStep - Math.PI / 2;
            const endAngle = (end + 1) * angleStep - Math.PI / 2;

            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.fillStyle = domainColorMap[domain] || 'rgba(0,0,0,0.05)';
            ctx.arc(cx, cy, radius, startAngle, endAngle);
            ctx.closePath();
            ctx.fill();
        });
    }
};

const chartOptions = computed(() => ({
    responsive: true,
    maintainAspectRatio: false,

    plugins: {
        legend: { display: false },

        tooltip: {
            enabled: true,
            padding: 10,
            boxPadding: 4,
            displayColors: true,
            usePointStyle: true,

            backgroundColor: 'rgba(30, 30, 30, 0.9)',
            titleColor: '#fff',
            bodyColor: '#e5e7eb',
            borderColor: 'rgba(255, 255, 255, 0.15)',
            borderWidth: 1,
            cornerRadius: 8,

            callbacks: {
                title: (ctx) => ctx[0].label,
                label: (ctx) => {
                    const sub = ctx.label;
                    const val = ctx.raw;
                    const domain = getDomain(sub);

                    return [
                        `Subdomain: ${sub}`,
                        `Domain: ${domain}`,
                        `Score: ${val}`
                    ];
                },
                labelColor: (ctx) => {
                    const sub = ctx.label;
                    const domain = getDomain(sub);

                    return {
                        backgroundColor: domainColorMap[domain] || '#6b7280',
                        borderColor: domainColorMap[domain] || '#6b7280',
                        borderWidth: 2,
                        borderRadius: 4
                    };
                }
            }
        },

        colorfulSectors: true,
    },

    scales: {
        r: {
            beginAtZero: true,
            min: 0,
            max: 5,

            ticks: {
                stepSize: 1,
                showLabelBackdrop: false,
                color: '#4b5563'
            },

            // hide labels in Mobile
            pointLabels: {
                display: !isMobile.value,
                color: '#111827',
                font: { size: 16, weight: '400' }
            }
        }
    },

    elements: {
        line: { tension: 0.25 },
        point: {
            radius: 5,
            hitRadius: 18
        }
    }
}));

</script>

<template>
<Radar
    ref="chartRef"
    :data="chartData"
    :options="chartOptions"
    :plugins="[colorfulSectorsPlugin]" />
</template>
